import PropTypes from "prop-types";
import Button from "react-bootstrap/Button";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import { useNavigate } from "react-router-dom";
import Time from "../../../../components/Time";
import TooltipWrapper from "../../../../enhancers/TooltipWrapper";
import { AnalysisButtonText } from "../../../../lib/enums";
import AnalysisTableIcon from "./AnalysisTableIcon";
import AnalysisTableResult from "./AnalysisTableResult";
import AnalysisTableRowItem from "./AnalysisTableRowItem";
import AnalysisTableSpinner from "./AnalysisTableSpinner";
import { ANALYSIS_METHOD } from "./consts";

const bsButtonStyle = {
  fontSize: "0.785em",
  letterSpacing: "1px",
  margin: "4px 0"
};

const getButton = props => {
  const buttonData = {
    text: "",
    style: "primary"
  };
  if (props.fileTypeSupported === false || props.isConfigured === false) {
    buttonData.text = AnalysisButtonText.CONFIGURE;
    buttonData.style = "secondary";
  } else if (props.neverSubmitted || props.fileOversized) {
    if (
      props.shortName === ANALYSIS_METHOD.CLOUD.SHORT_NAME ||
      props.shortName === ANALYSIS_METHOD.RL_SANDBOX.SHORT_NAME
    ) {
      buttonData.text = AnalysisButtonText.UPLOAD;
      buttonData.style = "secondary";
    } else {
      buttonData.text = AnalysisButtonText.ANALYZE;
    }
  } else {
    buttonData.text = AnalysisButtonText.REANALYZE;
  }
  return buttonData;
};

const HelpMessage = ({ helpMessage, actionButtonDisabled }) => (
  <div className="analysis-table__highlight-text">
    {helpMessage}&nbsp;{!actionButtonDisabled && `→`}
  </div>
);

HelpMessage.propTypes = {
  helpMessage: PropTypes.string.isRequired,
  actionButtonDisabled: PropTypes.bool.isRequired
};

const AnalysisTableRow = props => {
  const {
    icon,
    title,
    titleIcon,
    shortName,
    preview,
    isProcessing = false,
    isConfigured = true,
    isExtractedFile = false,
    result,
    lastAnalysisTime,
    newTiCoreVersion = false,
    fileTypeSupported = true,
    cloudIsOutdated = false,
    neverSubmitted = false,
    fileOversized = false,
    helpMessage = "",
    errorMessage,
    additionalMessage = "",
    noImpactOnClassification = false,
    noImpactMessage = "Does not impact final classification",
    classification,
    isDecisiveClassificationReason,
    reportUrl = null,
    isCloudOverride,
    noButton,
    analyzingText,
    cloudNavigate
  } = props;

  const navigate = useNavigate();

  const buttonStyle = getButton(props).style;
  const buttonText = getButton(props).text;

  const showNoImpactMessage =
    noImpactOnClassification && !fileOversized && result;

  const showHelpMessage =
    !!helpMessage &&
    (!fileTypeSupported ||
      !isConfigured ||
      newTiCoreVersion ||
      cloudIsOutdated ||
      neverSubmitted ||
      fileOversized ||
      helpMessage !== "Reported") &&
    !isProcessing;

  const actionButtonDisabled = Boolean(
    isProcessing || isExtractedFile || fileOversized
  );

  // NOTE: not capitalize error message, when message is "Sample was never submitted"
  const errorResult =
    errorMessage && !neverSubmitted ? (
      <span className="u-capitalize">{errorMessage}</span>
    ) : errorMessage && neverSubmitted ? (
      <>{errorMessage}</>
    ) : (
      ""
    );

  const resultColumnData =
    errorResult ||
    (result && (!neverSubmitted || isCloudOverride) && (
      <AnalysisTableResult
        resultData={result || additionalMessage}
        showHelpMessage={showHelpMessage}
        isCloudOverride={isCloudOverride}
        analysis={shortName}
      />
    ));

  const decisiveReasonClassName = isDecisiveClassificationReason
    ? `analysis-table__row--decisive-reason analysis-table__row--decisive-reason-${classification}`
    : "";

  const showDecisiveReasonLabel =
    isDecisiveClassificationReason && classification !== "unknown";

  // remove link if file type is not supported as it leads to non existing page
  const addLinkToTableRow = fileTypeSupported && resultColumnData && reportUrl;

  const tableCellLinkClass = addLinkToTableRow
    ? "analysis-table__cell-link"
    : null;

  const openReportPage = () => {
    if (!addLinkToTableRow) return;
    if (cloudNavigate) {
      cloudNavigate();
      return;
    }
    navigate(reportUrl);
  };

  const isHelpMessageAvailable =
    showHelpMessage &&
    (!resultColumnData ||
      shortName !== ANALYSIS_METHOD.RL_SANDBOX.SHORT_NAME ||
      showNoImpactMessage ||
      isCloudOverride);

  return (
    <tr
      className={`${decisiveReasonClassName} ${
        !isConfigured && !result ? "analysis-table__row--disabled" : ""
      }`}
    >
      <AnalysisTableRowItem
        centered
        rowClassName={`analysis-table__icon-cell ${tableCellLinkClass}`}
        onClick={openReportPage}
      >
        <AnalysisTableIcon icon={icon} />
      </AnalysisTableRowItem>

      <AnalysisTableRowItem
        rowClassName={`analysis-table__title-cell ${tableCellLinkClass}`}
        onClick={openReportPage}
      >
        {preview && <div className="analysis-table__label">New</div>}
        <span>
          {titleIcon && <AnalysisTableIcon icon={titleIcon} />}
          {title}
        </span>
        {showDecisiveReasonLabel && (
          <div className="analysis-table__decisive-reason-label">
            *Decisive Classification Reason
          </div>
        )}
      </AnalysisTableRowItem>

      <AnalysisTableRowItem
        centered
        rowClassName={`analysis-table__cell--analysis-result ${tableCellLinkClass}`}
        onClick={openReportPage}
      >
        <div className="analysis-table__cell-container--narrow">
          {isProcessing ? (
            <AnalysisTableSpinner icon={icon} text={analyzingText} />
          ) : (
            <>
              {resultColumnData}
              {showNoImpactMessage && (
                <div className="analysis-table__help-message">
                  {noImpactMessage}
                </div>
              )}
            </>
          )}
        </div>
      </AnalysisTableRowItem>

      <AnalysisTableRowItem>
        {lastAnalysisTime && !isProcessing && isConfigured && !neverSubmitted && (
          <div>
            <Time time={lastAnalysisTime} />
          </div>
        )}
        {isHelpMessageAvailable && (
          <HelpMessage
            actionButtonDisabled={actionButtonDisabled}
            helpMessage={helpMessage}
          />
        )}
      </AnalysisTableRowItem>

      <AnalysisTableRowItem centered>
        <TooltipWrapper
          condition={
            props.isExtractedFile === true &&
            shortName === ANALYSIS_METHOD.STATIC.SHORT_NAME
          }
          wrapper={children => (
            <OverlayTrigger
              overlay={
                <Tooltip id="tiCoreReanalyze">
                  Extracted samples cannot be reanalyzed with Spectra Core.
                </Tooltip>
              }
              placement="top"
            >
              <div>{children}</div>
            </OverlayTrigger>
          )}
        >
          {!noButton && (
            <Button
              id={`${shortName}--reanalyze-btn`}
              variant={buttonStyle}
              size="sm"
              disabled={actionButtonDisabled}
              onClick={() => props.handleAction({ value: buttonText })}
              style={bsButtonStyle}
              className="analysis-table__action-btn btn--uppercase"
            >
              {buttonText.toUpperCase()}
            </Button>
          )}
        </TooltipWrapper>
      </AnalysisTableRowItem>
    </tr>
  );
};

AnalysisTableRow.propTypes = {
  noButton: PropTypes.bool,
  icon: PropTypes.string,
  titleIcon: PropTypes.string,
  title: PropTypes.string,
  shortName: PropTypes.string,
  preview: PropTypes.bool,
  isConfigured: PropTypes.bool,
  isProcessing: PropTypes.bool,
  result: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  lastAnalysisTime: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.instanceOf(Date)
  ]),
  newTiCoreVersion: PropTypes.bool,
  fileTypeSupported: PropTypes.bool,
  cloudIsOutdated: PropTypes.bool,
  neverSubmitted: PropTypes.bool,
  fileOversized: PropTypes.bool,
  helpMessage: PropTypes.string,
  errorMessage: PropTypes.string,
  handleAction: PropTypes.func,
  isExtractedFile: PropTypes.bool,
  additionalMessage: PropTypes.string,
  noImpactOnClassification: PropTypes.bool,
  noImpactMessage: PropTypes.string,
  classification: PropTypes.string,
  isDecisiveClassificationReason: PropTypes.bool,
  reportUrl: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  isCloudOverride: PropTypes.bool,
  analyzingText: PropTypes.string,
  cloudNavigate: PropTypes.func
};

export default AnalysisTableRow;
